package features.booking

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/** Un movimiento registrado en la bitácora de auditoría (quién hizo qué). */
data class MovimientoAuditoria(
    val id: String = "",
    val accion: String = "",
    val operadorUid: String = "",
    val operadorNombre: String = "",
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val reservaId: String = "",
    val pagoId: String = "",
    val asientos: String = "",
    val monto: Int = 0,
    val targetUid: String = "",
    val detalle: String = "",
    val pasajeros: String = "",   // nombres de pasajeros (rastreo de venta efectivo/manual)
    val email: String = "",       // correo del titular
    val metodoPago: String = "",  // efectivo / spei / etc
    val timestamp: Long = 0L
)

private data class AccionEstilo(val etiqueta: String, val color: Color, val icono: androidx.compose.ui.graphics.vector.ImageVector)

private fun estiloDeAccion(accion: String): AccionEstilo = when (accion) {
    "efectivo_creado" -> AccionEstilo("Efectivo creado", SmartVanColors.VerdeOscuro, Icons.Filled.Payments)
    "efectivo_confirmado" -> AccionEstilo("Efectivo confirmado", Color(0xFF1565C0), Icons.Filled.CheckCircle)
    "manual_confirmado" -> AccionEstilo("Transferencia confirmada", Color(0xFF1565C0), Icons.Filled.CheckCircle)
    "manual_declinado" -> AccionEstilo("Transferencia declinada", Color(0xFFC62828), Icons.Filled.Close)
    "reserva_cancelada" -> AccionEstilo("Reserva cancelada", SmartVanColors.NaranjaDeep, Icons.Filled.Delete)
    "viaje_cancelado" -> AccionEstilo("Viaje cancelado", Color(0xFFC62828), Icons.Filled.Delete)
    "cuenta_creada" -> AccionEstilo("Cuenta creada", SmartVanColors.VerdeOscuro, Icons.Filled.Person)
    "cuenta_eliminada" -> AccionEstilo("Cuenta eliminada", Color(0xFFC62828), Icons.Filled.Delete)
    "password_reseteado" -> AccionEstilo("Contraseña reseteada", Color(0xFF1565C0), Icons.Filled.Lock)
    "rol_cambiado" -> AccionEstilo("Rol cambiado", Color(0xFF6A1B9A), Icons.Filled.Settings)
    "factura_enviada" -> AccionEstilo("Factura enviada al contador", Color(0xFF7B1FA2), Icons.Filled.Email)
    "factura_facturada" -> AccionEstilo("Factura emitida", SmartVanColors.VerdeOscuro, Icons.Filled.Receipt)
    else -> AccionEstilo(accion.replace("_", " ").ifBlank { "Movimiento" }, Color.Gray, Icons.Filled.Info)
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuditoriaView(viewModel: AdminViewModel) {
    val movimientos by viewModel.auditoria.collectAsState()
    val cargando by viewModel.cargandoAuditoria.collectAsState()
    var filtroAccion by remember { mutableStateOf("Todos") }
    var expanded by remember { mutableStateOf(false) }
    var busqueda by remember { mutableStateOf("") }

    LaunchedEffect(Unit) { viewModel.cargarAuditoria() }

    val opciones = listOf("Todos", "efectivo_creado", "efectivo_confirmado", "manual_confirmado", "manual_declinado", "reserva_cancelada", "viaje_cancelado", "cuenta_creada", "cuenta_eliminada", "password_reseteado", "rol_cambiado", "factura_enviada", "factura_facturada")

    val filtrados = movimientos.filter { m ->
        val matchAccion = filtroAccion == "Todos" || m.accion == filtroAccion
        val matchBusqueda = busqueda.isBlank() ||
            m.operadorNombre.contains(busqueda, ignoreCase = true) ||
            m.ruta.contains(busqueda, ignoreCase = true) ||
            m.reservaId.contains(busqueda, ignoreCase = true)
        matchAccion && matchBusqueda
    }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
            Icon(Icons.Filled.Receipt, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(28.dp))
            Spacer(modifier = Modifier.width(8.dp))
            Text("Auditoría de movimientos", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            Spacer(modifier = Modifier.weight(1f))
            IconButton(onClick = { viewModel.cargarAuditoria() }) {
                Icon(Icons.Filled.Refresh, contentDescription = "Actualizar", tint = SmartVanColors.CyanAccent)
            }
        }
        Text("Quién creó, confirmó o canceló cada venta. El operador se firma de forma segura en el servidor.",
            color = Color.Gray, style = MaterialTheme.typography.bodySmall)
        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = busqueda,
            onValueChange = { busqueda = it },
            modifier = Modifier.fillMaxWidth(),
            placeholder = { Text("Buscar por operador, ruta o reserva...") },
            leadingIcon = { Icon(Icons.Filled.Search, contentDescription = null, tint = Color.Gray) },
            shape = RoundedCornerShape(12.dp),
            singleLine = true
        )
        Spacer(modifier = Modifier.height(8.dp))

        ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
            OutlinedTextField(
                value = if (filtroAccion == "Todos") "Todas las acciones" else estiloDeAccion(filtroAccion).etiqueta,
                onValueChange = {},
                readOnly = true,
                label = { Text("Acción", fontSize = 11.sp) },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
                modifier = Modifier.menuAnchor().fillMaxWidth(),
                shape = RoundedCornerShape(10.dp)
            )
            ExposedDropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
                opciones.forEach { op ->
                    val label = if (op == "Todos") "Todas las acciones" else estiloDeAccion(op).etiqueta
                    DropdownMenuItem(text = { Text(label) }, onClick = { filtroAccion = op; expanded = false })
                }
            }
        }
        Spacer(modifier = Modifier.height(12.dp))

        if (cargando) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
            }
        } else if (filtrados.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                Text("Sin movimientos registrados con estos filtros.", color = Color.Gray, fontStyle = FontStyle.Italic)
            }
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                items(filtrados, key = { it.id }) { m -> MovimientoCard(m) }
            }
        }
    }
}

@Composable
private fun MovimientoCard(m: MovimientoAuditoria) {
    val estilo = estiloDeAccion(m.accion)
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, estilo.color.copy(alpha = 0.3f)),
        colors = CardDefaults.cardColors(containerColor = Color.White)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                Icon(estilo.icono, contentDescription = null, tint = estilo.color, modifier = Modifier.size(18.dp))
                Spacer(modifier = Modifier.width(6.dp))
                Text(estilo.etiqueta, fontWeight = FontWeight.Bold, color = estilo.color, style = MaterialTheme.typography.bodyMedium)
                Spacer(modifier = Modifier.weight(1f))
                if (m.monto > 0) Text("$${m.monto} MXN", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            }
            Spacer(modifier = Modifier.height(6.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Filled.Person, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp))
                Spacer(modifier = Modifier.width(4.dp))
                Text(
                    "Operador: ${m.operadorNombre.ifBlank { "(sin nombre)" }}",
                    fontWeight = FontWeight.Medium, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall
                )
            }
            if (m.detalle.isNotBlank()) {
                Text(m.detalle, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall)
            }
            if (m.ruta.isNotBlank()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Map, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(13.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("${m.ruta} • ${m.fecha} ${m.horario}".trim(), color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                }
            }
            if (m.asientos.isNotBlank()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.EventSeat, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(13.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Asientos: ${m.asientos}${if (m.metodoPago.isNotBlank()) "  •  ${m.metodoPago.uppercase()}" else ""}",
                        color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                }
            }
            if (m.pasajeros.isNotBlank()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.People, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(13.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(m.pasajeros, color = Color.Gray, style = MaterialTheme.typography.bodySmall, maxLines = 2)
                }
            }
            if (m.email.isNotBlank()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Email, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(13.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(m.email, color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                }
            }
            // ID de reserva/pago: clave para rastrear y ajustar una venta específica después.
            if (m.reservaId.isNotBlank() || m.pagoId.isNotBlank()) {
                Text(
                    "Ref: ${m.reservaId.ifBlank { m.pagoId }}",
                    color = Color.Gray, style = MaterialTheme.typography.labelSmall,
                    fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace
                )
            }
            if (m.timestamp > 0L) {
                Text(
                    org.example.abigail.formatMillisToDate(m.timestamp) + " " + org.example.abigail.formatMillisToTime(m.timestamp),
                    color = Color.Gray, style = MaterialTheme.typography.labelSmall
                )
            }
        }
    }
}
